/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import * as Func from 'services/Function';
import MDButton from 'components/MDButton';
import { Backdrop, CircularProgress, Icon } from '@mui/material';
import Swal from 'sweetalert2';
import { FetchData } from 'services/Request';
import SearchProduct from 'layouts/credit_sale/SearchProduct';

const CreditSaleList = forwardRef(({ record, value_type, status, reload, id }, ref) => {
  const mod = 'credit_sales_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };
  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            reload();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const add = (item) => {
    FetchData('PUT', `/${mod}`, app.token, {
      credit_sales_id: id,
      barcode_id: item.id,
      quantity: item.quantity,
    }).then((result) => {
      if (result.status === 'ok') {
        reload();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const expireDisplay = (item) => {
    if (item.is_expire === 'N') return '-';

    return Func.dateDisplay(item.date_expire);
  };

  const btnDel = (item) => {
    if (status === 'cancel' || status === 'success') return null;

    return (
      <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell>LOT</TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            จำนวนคืน
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            ราคา
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            รวม
          </TableCell>
          <TableCell align='center' width={90} className='nowrap'>
            ลบ
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {record.map((row, index) => (
          <TableRow key={row.id} hover>
            <TableCell align='center'>{index + 1}</TableCell>
            <TableCell>{Func.imageDisplay(row)}</TableCell>
            <TableCell>
              <MDBox className='nowrap'>
                {row.code} <span className='color-default'>{row.sku}</span>
              </MDBox>
              <MDBox className='nowrap'>{row.name}</MDBox>
            </TableCell>
            <TableCell>
              <MDBox className='nowrap'>{row.lot}</MDBox>
              <MDBox className='nowrap'>{expireDisplay(row)}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>{Func.numberDisplay(row.quantity)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>
                {value_type
                  ? Func.numberDisplay(value_type === 'cost' ? row.cost : row.price)
                  : '-'}
              </MDBox>
              <MDBox>บาท</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>
                {value_type
                  ? Func.numberDisplay(value_type === 'cost' ? row.amount_cost : row.amount_price)
                  : '-'}
              </MDBox>
              <MDBox>บาท</MDBox>
            </TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderSummary = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมจำนวน</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>
                {value_type
                  ? Func.numberDisplay(
                      record.reduce(
                        (a, b) =>
                          a + (value_type === 'cost' ? Number(b.quantity) : Number(b.quantity)),
                        0
                      )
                    )
                  : '-'}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>ชิ้น</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมรายการ</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>
                {value_type ? Func.numberDisplay(Number(record.length)) : '-'}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>รายการ</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {value_type
                  ? Func.numberDisplay(
                      record.reduce(
                        (a, b) =>
                          a +
                          (value_type === 'cost' ? Number(b.amount_cost) : Number(b.amount_price)),
                        0
                      ),
                      2
                    )
                  : '-'}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox pt={3} pl={2}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>
          {status === 'edit' && (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1} p={2}>
                <SearchProduct add={add} />
              </MDBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
              <Table aria-label='simple table'>
                {renderHeader()}
                {renderRecord()}
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>{renderSummary()}</TableContainer>
          </Grid>

          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color='inherit' />
          </Backdrop>
          {notify}
        </Grid>
      </MDBox>
    );
  };

  return <div>{renderList()}</div>;
});

CreditSaleList.propTypes = {
  record: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      sku: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      lot: PropTypes.string.isRequired,
      is_expire: PropTypes.string.isRequired,
      date_expire: PropTypes.string.isRequired,
      quantity: PropTypes.string.isRequired,
      unit_name: PropTypes.string.isRequired,
      cost: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
    })
  ).isRequired,
  value_type: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  reload: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default CreditSaleList;
